import type { ApplyCanvasPlanArgsType } from "@akem/shared";
import type { SecondOrderDomain } from "../../market/mockData.js";

export interface ProfileCanvasPlanOptions {
  profileId: string;
  profileName?: string;
  ticker?: string;
}

const SECOND_ORDER_DOMAIN_ORDER: SecondOrderDomain[] = [
  "customers",
  "suppliers",
  "marketTrends",
  "politics",
  "regulatory",
  "mna",
  "logistics",
  "themes",
];

export const PROFILE_CANVAS_PLAN_VERSION = "2025-10-09-intelligence-v1";

const GRID_GAP = 40;
const COLUMN_STACK_GAP = 32;
const BASE_X = 80;
const BASE_Y = 48;
const PRIMARY_COLUMN_WIDTH = 880;
const SECONDARY_COLUMN_WIDTH = 520;
const SECONDARY_COLUMN_X = BASE_X + PRIMARY_COLUMN_WIDTH + GRID_GAP;
const FULL_WIDTH = PRIMARY_COLUMN_WIDTH + GRID_GAP + SECONDARY_COLUMN_WIDTH;

const PROFILE_HEADER_SIZE = { width: FULL_WIDTH, height: 180 };
const AKEM_TICKER_SIZE = { width: FULL_WIDTH, height: 220 };
const PRICE_CHART_SIZE = { width: PRIMARY_COLUMN_WIDTH, height: 360 };
const FINANCIALS_SIZE = { width: SECONDARY_COLUMN_WIDTH, height: 380 };
const KEY_STATS_SIZE = { width: SECONDARY_COLUMN_WIDTH, height: 260 };
const CALENDAR_SIZE = { width: FULL_WIDTH, height: 320 };
const NEWS_SENTIMENT_SIZE = { width: FULL_WIDTH, height: 360 };
const SECOND_ORDER_CARD_SIZE = { width: 340, height: 360 };
const SECOND_ORDER_COLUMNS = 3;
const SECOND_ORDER_HORIZONTAL_GAP = 32;
const SECOND_ORDER_VERTICAL_GAP = 32;

const PROFILE_SECOND_ORDER_VISIBLE = SECOND_ORDER_DOMAIN_ORDER.slice(0, 6);

const DOMAIN_TITLES: Record<SecondOrderDomain, string> = {
  customers: "Customers",
  suppliers: "Suppliers",
  marketTrends: "Market Trends",
  politics: "Politics",
  regulatory: "Regulatory",
  mna: "M&A",
  logistics: "Logistics",
  themes: "Themes",
};

type LayoutEntry = {
  position: { x: number; y: number };
  size: { width: number; height: number };
};

type SecondOrderLayout = LayoutEntry & { domain: SecondOrderDomain };

const computeProfileLayout = (): {
  singles: Record<string, LayoutEntry>;
  secondOrder: SecondOrderLayout[];
} => {
  const singles: Record<string, LayoutEntry> = {};

  singles.profileHeader = {
    position: { x: BASE_X, y: BASE_Y },
    size: PROFILE_HEADER_SIZE,
  };

  const headerBottom = singles.profileHeader.position.y + PROFILE_HEADER_SIZE.height;

  singles.akemTicker = {
    position: { x: BASE_X, y: headerBottom + GRID_GAP },
    size: AKEM_TICKER_SIZE,
  };

  const tickerBottom = singles.akemTicker.position.y + AKEM_TICKER_SIZE.height;

  const priceRowY = tickerBottom + GRID_GAP;
  singles.priceChart = {
    position: { x: BASE_X, y: priceRowY },
    size: PRICE_CHART_SIZE,
  };

  singles.financials = {
    position: { x: SECONDARY_COLUMN_X, y: priceRowY },
    size: FINANCIALS_SIZE,
  };

  const priceBottom = priceRowY + PRICE_CHART_SIZE.height;
  const financialsBottom = priceRowY + FINANCIALS_SIZE.height;

  singles.keyStats = {
    position: {
      x: SECONDARY_COLUMN_X,
      y: singles.financials.position.y + FINANCIALS_SIZE.height + COLUMN_STACK_GAP,
    },
    size: KEY_STATS_SIZE,
  };

  const keyStatsBottom =
    singles.keyStats.position.y + singles.keyStats.size.height;

  const lowerBandBottom = Math.max(priceBottom, keyStatsBottom);
  const calendarY = lowerBandBottom + GRID_GAP;

  singles.calendar = {
    position: { x: BASE_X, y: calendarY },
    size: CALENDAR_SIZE,
  };

  const calendarBottom = calendarY + CALENDAR_SIZE.height;
  const rightColumnBottom = Math.max(financialsBottom, keyStatsBottom);
  const secondOrderStartY = Math.max(calendarBottom, rightColumnBottom) + GRID_GAP;

  const secondOrderGridWidth =
    SECOND_ORDER_COLUMNS * SECOND_ORDER_CARD_SIZE.width +
    (SECOND_ORDER_COLUMNS - 1) * SECOND_ORDER_HORIZONTAL_GAP;
  const secondOrderStartX =
    BASE_X + Math.max(0, Math.round((FULL_WIDTH - secondOrderGridWidth) / 2));

  const secondOrder = PROFILE_SECOND_ORDER_VISIBLE.map((domain, index) => {
    const column = index % SECOND_ORDER_COLUMNS;
    const row = Math.floor(index / SECOND_ORDER_COLUMNS);
    return {
      domain,
      position: {
        x:
          secondOrderStartX +
          column * (SECOND_ORDER_CARD_SIZE.width + SECOND_ORDER_HORIZONTAL_GAP),
        y:
          secondOrderStartY +
          row * (SECOND_ORDER_CARD_SIZE.height + SECOND_ORDER_VERTICAL_GAP),
      },
      size: SECOND_ORDER_CARD_SIZE,
    } satisfies SecondOrderLayout;
  });

  const secondOrderRows = Math.ceil(
    PROFILE_SECOND_ORDER_VISIBLE.length / SECOND_ORDER_COLUMNS
  );
  const secondOrderBottom =
    secondOrderRows > 0
      ? secondOrderStartY +
        (secondOrderRows - 1) *
          (SECOND_ORDER_CARD_SIZE.height + SECOND_ORDER_VERTICAL_GAP) +
        SECOND_ORDER_CARD_SIZE.height
      : secondOrderStartY;

  singles.newsSentiment = {
    position: { x: BASE_X, y: secondOrderBottom + GRID_GAP },
    size: NEWS_SENTIMENT_SIZE,
  };

  return { singles, secondOrder };
};

const PROFILE_LAYOUT = computeProfileLayout();

export const PROFILE_WIDGET_LAYOUTS: Record<string, LayoutEntry> =
  PROFILE_LAYOUT.singles;

export const PROFILE_SECOND_ORDER_LAYOUTS: SecondOrderLayout[] =
  PROFILE_LAYOUT.secondOrder;

export function buildProfileCanvasPlan(
  options: ProfileCanvasPlanOptions
): ApplyCanvasPlanArgsType {
  const { profileId, profileName, ticker } = options;
  const displayName = profileName?.trim() || profileId;
  const tickerUpper = ticker?.toUpperCase();

  const widgets: NonNullable<ApplyCanvasPlanArgsType["plan"]>["widgets"] = [];

  const pushWidget = (
    type: string,
    title: string,
    position: { x: number; y: number },
    config: Record<string, unknown>
  ) => {
    widgets.push({
      type,
      title,
      position,
      config,
    });
  };

  pushWidget("profileHeader", `${displayName} Overview`, PROFILE_WIDGET_LAYOUTS.profileHeader.position, {
    title: `${displayName} Overview`,
    profileId,
    ticker: tickerUpper,
  });

  pushWidget("akemTicker", "Akem Highlights", PROFILE_WIDGET_LAYOUTS.akemTicker.position, {
    title: "Akem Highlights",
    profileId,
  });

  pushWidget("priceChart", "Price", PROFILE_WIDGET_LAYOUTS.priceChart.position, {
    title: "Price",
    profileId,
    ticker: tickerUpper,
  });

  pushWidget("financials", "Financials", PROFILE_WIDGET_LAYOUTS.financials.position, {
    title: "Financials",
    profileId,
    ticker: tickerUpper,
  });

  pushWidget("keyStats", "Key Ratios Snapshot", PROFILE_WIDGET_LAYOUTS.keyStats.position, {
    title: "Key Ratios Snapshot",
    profileId,
    ticker: tickerUpper,
  });

  pushWidget("calendar", "Company Calendar", PROFILE_WIDGET_LAYOUTS.calendar.position, {
    title: "Company Calendar",
    profileId,
  });

  PROFILE_SECOND_ORDER_LAYOUTS.forEach(({ domain, position }) => {
    pushWidget(
      "akemIntelligence",
      DOMAIN_TITLES[domain],
      position,
      {
        title: DOMAIN_TITLES[domain],
        domain,
        profileId,
      }
    );
  });

  pushWidget(
    "newsSentiment",
    "News & Sentiment",
    PROFILE_WIDGET_LAYOUTS.newsSentiment.position,
    {
      title: "News & Sentiment",
      profileId,
      ticker: tickerUpper,
    }
  );

  return {
    plan: {
      widgets,
      connections: [],
    },
    canvas: {
      key: `profile:${profileId}`,
      kind: "profile",
      name: `Profile · ${displayName}`,
    },
  };
}

export const profileSecondOrderDomains = PROFILE_SECOND_ORDER_VISIBLE;
